import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Search, Star, ArrowUp, ArrowDown, FolderTree, Archive, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cx } from '@/lib/format';
import { slugify } from '@/data/nav';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ImageField } from '@/components/common/ImageUploadField';

const TYPES = ['category', 'room', 'style', 'material', 'festival', 'collection'];

const EMPTY = {
  name: '', slug: '', type: 'category', parent: '',
  short_description: '', description: '',
  image: '', hero_image: '', hero_image_mobile: '', og_image: '',
  active: true, featured: false, sort_order: 0,
  meta_title: '', meta_description: '', keywords: '',
  canonical_url: '', og_title: '', og_description: '', robots: 'index,follow',
};

// Map any category doc (possibly older/partial) into a complete CategoryInput payload.
// PUT replaces the document, so we must always send every field.
const toPayload = (c, overrides = {}) => ({
  name: c.name || '',
  slug: c.slug || '',
  type: c.type || 'category',
  parent: c.parent || '',
  short_description: c.short_description || '',
  description: c.description || '',
  image: c.image || '',
  hero_image: c.hero_image || '',
  hero_image_mobile: c.hero_image_mobile || '',
  og_image: c.og_image || '',
  active: c.active !== false,
  featured: !!c.featured,
  sort_order: Number(c.sort_order || 0),
  meta_title: c.meta_title || '',
  meta_description: c.meta_description || '',
  keywords: c.keywords || '',
  canonical_url: c.canonical_url || '',
  og_title: c.og_title || '',
  og_description: c.og_description || '',
  robots: c.robots || 'index,follow',
  ...overrides,
});

const field = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-[hsl(var(--gaze-gold))]';
const labelCls = 'text-xs font-medium text-muted-foreground';

function Counter({ value, min, max }) {
  const len = (value || '').length;
  const ok = len >= (min || 0) && len <= max;
  return <span className={cx('text-[11px]', ok ? 'text-emerald-600' : 'text-amber-600')}>{len}/{max}{min ? ` · aim ${min}-${max}` : ''}</span>;
}

const TypeBadge = ({ t }) => (
  <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--primary))]">{t}</span>
);

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('category');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
  const [featuredFilter, setFeaturedFilter] = useState('all'); // all | featured | not

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [tab, setTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null); // {cat, count}
  const [selected, setSelected] = useState([]); // ids for bulk actions
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/categories').then((r) => setCats(r.data || [])).catch(() => toast.error('Could not load categories')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const m = {};
    cats.forEach((c) => { m[c.type] = (m[c.type] || 0) + 1; });
    return m;
  }, [cats]);

  // Ordered list for the active type (used for move up/down + display)
  const typeList = useMemo(() => (
    cats.filter((c) => c.type === typeFilter)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name))
  ), [cats, typeFilter]);

  const visible = useMemo(() => {
    const query = q.trim().toLowerCase();
    return typeList.filter((c) => {
      if (query && !(`${c.name} ${c.slug}`.toLowerCase().includes(query))) return false;
      if (statusFilter === 'active' && c.active === false) return false;
      if (statusFilter === 'inactive' && c.active !== false) return false;
      if (featuredFilter === 'featured' && !c.featured) return false;
      if (featuredFilter === 'not' && c.featured) return false;
      return true;
    });
  }, [typeList, q, statusFilter, featuredFilter]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, type: typeFilter });
    setSlugTouched(false); setTab('general'); setDirty(false); setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm(toPayload(c));
    setSlugTouched(true); setTab('general'); setDirty(false); setOpen(true);
  };

  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  const onNameChange = (name) => {
    setForm((f) => ({ ...f, name, slug: (!editing && !slugTouched) ? slugify(name) : f.slug }));
    setDirty(true);
  };

  const requestClose = (next) => {
    if (!next && dirty && !window.confirm('Discard unsaved changes?')) return;
    setOpen(next);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Category name is required'); setTab('general'); return; }
    const payload = toPayload(form, { slug: (form.slug || slugify(form.name)).trim().toLowerCase(), sort_order: Number(form.sort_order || 0) });
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, payload);
      else await api.post('/admin/categories', payload);
      toast.success(editing ? 'Category updated' : 'Category created');
      setDirty(false); setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed — please check the fields');
    } finally { setSaving(false); }
  };

  // Inline quick toggles — send the FULL object so nothing is lost
  const quickPatch = async (c, overrides) => {
    try {
      await api.put(`/admin/categories/${c.id}`, toPayload(c, overrides));
      setCats((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...overrides } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const move = async (c, dir) => {
    const list = [...typeList];
    const i = list.findIndex((x) => x.id === c.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    const ordered_ids = list.map((x) => x.id);
    // optimistic
    setCats((prev) => prev.map((x) => {
      const idx = ordered_ids.indexOf(x.id);
      return idx >= 0 && x.type === typeFilter ? { ...x, sort_order: idx } : x;
    }));
    try { await api.post('/admin/categories/reorder', { type: typeFilter, ordered_ids }); }
    catch { toast.error('Reorder failed'); load(); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success('Category deleted'); load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Delete failed';
      if (e?.response?.status === 400 && (c.product_count || 0) > 0) {
        setArchiveTarget({ cat: c, count: c.product_count });
      } else {
        toast.error(detail);
      }
    }
  };

  const archive = async () => {
    if (!archiveTarget) return;
    await quickPatch(archiveTarget.cat, { active: false });
    toast.success(`"${archiveTarget.cat.name}" archived (set inactive)`);
    setArchiveTarget(null);
  };

  // ---- Bulk selection + actions ----
  useEffect(() => { setSelected([]); }, [typeFilter]);

  const toggleSelect = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allVisibleSelected = visible.length > 0 && visible.every((c) => selected.includes(c.id));
  const toggleSelectAll = () => setSelected(allVisibleSelected ? [] : visible.map((c) => c.id));

  const BULK_LABELS = { activate: 'activated', archive: 'archived (set inactive)', feature: 'marked featured', unfeature: 'removed from featured' };
  const bulkAct = async (action) => {
    if (selected.length === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      const r = await api.post('/admin/categories/bulk', { ids: selected, action });
      toast.success(`${r.data?.modified ?? selected.length} categor${(r.data?.modified ?? selected.length) === 1 ? 'y' : 'ies'} ${BULK_LABELS[action]}`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk update failed');
    } finally { setBulkBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Categories</h1>
          <p className="text-sm text-muted-foreground">Manage category content, images, SEO, featured status and ordering across all groups.</p>
        </div>
        <button onClick={openNew} data-testid="admin-category-new" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2.5 text-sm font-semibold text-white">
          <Plus size={16} /> New Category
        </button>
      </div>

      {/* Type tabs */}
      <div className="mt-5 flex flex-wrap gap-2">
        {TYPES.map((t) => (
          <button key={t} onClick={() => setTypeFilter(t)} data-testid={`cat-type-${t}`}
            className={cx('rounded-full border px-3.5 py-1.5 text-xs font-semibold capitalize',
              typeFilter === t ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white' : 'border-border bg-white text-foreground')}>
            {t} <span className={cx('ml-1', typeFilter === t ? 'text-white/70' : 'text-muted-foreground')}>{counts[t] || 0}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-white p-3">
        <div className="flex min-w-[200px] flex-1 items-center gap-2 rounded-lg border border-border px-3">
          <Search size={15} className="text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or slug" data-testid="admin-category-search" className="w-full bg-transparent py-2 text-sm outline-none" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="admin-category-status-filter">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={featuredFilter} onChange={(e) => setFeaturedFilter(e.target.value)} className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="admin-category-featured-filter">
          <option value="all">All</option>
          <option value="featured">Featured only</option>
          <option value="not">Not featured</option>
        </select>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-[hsl(var(--gaze-gold))] bg-[hsl(var(--gaze-ivory))]/60 p-3" data-testid="admin-category-bulk-bar">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold"><CheckCircle2 size={15} className="text-[hsl(var(--gaze-gold))]" /> {selected.length} selected</span>
          <div className="ml-auto flex flex-wrap gap-2">
            <button onClick={() => bulkAct('activate')} disabled={bulkBusy} data-testid="admin-category-bulk-activate" className="rounded-full bg-emerald-600 px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-60">Activate</button>
            <button onClick={() => bulkAct('archive')} disabled={bulkBusy} data-testid="admin-category-bulk-archive" className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-60"><Archive size={13} /> Archive</button>
            <button onClick={() => bulkAct('feature')} disabled={bulkBusy} data-testid="admin-category-bulk-feature" className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--gaze-gold))] px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-60"><Star size={13} /> Feature</button>
            <button onClick={() => bulkAct('unfeature')} disabled={bulkBusy} data-testid="admin-category-bulk-unfeature" className="rounded-full border border-border bg-white px-3.5 py-1.5 text-xs font-semibold disabled:opacity-60">Unfeature</button>
            <button onClick={() => setSelected([])} disabled={bulkBusy} data-testid="admin-category-bulk-clear" className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3.5 py-1.5 text-xs font-semibold text-muted-foreground disabled:opacity-60"><X size={13} /> Clear</button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading categories…</p>
      ) : visible.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-white p-10 text-center">
          <FolderTree className="mx-auto mb-3 text-muted-foreground" size={26} />
          <p className="text-sm text-muted-foreground">No categories match. Create one to get started.</p>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-white" data-testid="admin-categories-list">
          {/* header row (desktop) */}
          <div className="hidden grid-cols-[auto_auto_56px_1fr_auto_auto_auto_auto] items-center gap-3 border-b border-border bg-[hsl(var(--gaze-ivory))]/40 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:grid">
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} className="h-4 w-4 accent-[hsl(var(--primary))]" title="Select all" data-testid="admin-category-select-all" />
            <span>Order</span><span>Image</span><span>Category</span><span>Products</span><span>Featured</span><span>Status</span><span className="text-right">Actions</span>
          </div>
          {visible.map((c, idx) => (
            <div key={c.id} className={cx('grid grid-cols-[auto_auto_1fr_auto] items-center gap-3 border-b border-border px-4 py-3 last:border-0 md:grid-cols-[auto_auto_56px_1fr_auto_auto_auto_auto]', selected.includes(c.id) && 'bg-[hsl(var(--gaze-ivory))]/50')} data-testid="admin-category-row">
              {/* select */}
              <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid="admin-category-select" />
              {/* order controls */}
              <div className="flex flex-col">
                <button onClick={() => move(c, -1)} disabled={idx === 0 && q === '' && statusFilter === 'all' && featuredFilter === 'all'} className="grid h-6 w-6 place-items-center rounded text-muted-foreground hover:bg-[hsl(var(--gaze-ivory))] disabled:opacity-30" data-testid="admin-category-up"><ArrowUp size={14} /></button>
                <button onClick={() => move(c, 1)} className="grid h-6 w-6 place-items-center rounded text-muted-foreground hover:bg-[hsl(var(--gaze-ivory))] disabled:opacity-30" data-testid="admin-category-down"><ArrowDown size={14} /></button>
              </div>
              {/* thumbnail */}
              <div className="hidden h-14 w-14 overflow-hidden rounded-lg bg-[hsl(var(--gaze-ivory))] md:block">
                {(c.image || c.hero_image) ? <img src={c.image || c.hero_image} alt={c.name} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <div className="grid h-full w-full place-items-center text-muted-foreground"><ImageOff size={16} /></div>}
              </div>
              {/* name + slug */}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate font-medium">{c.name}</p>
                  <TypeBadge t={c.type} />
                </div>
                <p className="truncate text-xs text-muted-foreground">/{c.type === 'category' ? 'category' : c.type + (c.type.endsWith('s') ? '' : '')}/{c.slug}</p>
              </div>
              {/* product count */}
              <div className="hidden text-sm text-muted-foreground md:block">{c.product_count ?? 0}</div>
              {/* featured toggle */}
              <button onClick={() => quickPatch(c, { featured: !c.featured })} title="Toggle featured" data-testid="admin-category-featured-toggle"
                className={cx('grid h-8 w-8 place-items-center rounded-lg border', c.featured ? 'border-[hsl(var(--gaze-gold))] bg-[hsl(var(--gaze-gold))]/20 text-[hsl(var(--gaze-gold))]' : 'border-border text-muted-foreground')}>
                <Star size={15} fill={c.featured ? 'currentColor' : 'none'} />
              </button>
              {/* status toggle */}
              <button onClick={() => quickPatch(c, { active: c.active === false })} data-testid="admin-category-active-toggle"
                className={cx('rounded-full px-2.5 py-1 text-[11px] font-semibold', c.active === false ? 'bg-muted text-muted-foreground' : 'bg-emerald-50 text-emerald-700')}>
                {c.active === false ? 'Inactive' : 'Active'}
              </button>
              {/* actions */}
              <div className="flex justify-end gap-1">
                <button onClick={() => openEdit(c)} className="grid h-8 w-8 place-items-center rounded-lg border border-border" data-testid="admin-category-edit"><Pencil size={14} /></button>
                <button onClick={() => del(c)} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-destructive" data-testid="admin-category-delete"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor */}
      <Dialog open={open} onOpenChange={requestClose}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit · ${editing.name}` : 'New Category'}</DialogTitle>
            <DialogDescription className="sr-only">Category editor form</DialogDescription>
          </DialogHeader>

          {/* section tabs */}
          <div className="flex gap-1 rounded-xl bg-[hsl(var(--gaze-ivory))] p-1 text-sm">
            {['general', 'images', 'display', 'seo'].map((s) => (
              <button key={s} onClick={() => setTab(s)} data-testid={`cat-tab-${s}`}
                className={cx('flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold capitalize', tab === s ? 'bg-white shadow-sm' : 'text-muted-foreground')}>{s}</button>
            ))}
          </div>

          <div className="mt-4">
            {tab === 'general' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2"><label className={labelCls}>Name *</label><input value={form.name} onChange={(e) => onNameChange(e.target.value)} className={field} data-testid="admin-category-name" /></div>
                <div>
                  <label className={labelCls}>Slug</label>
                  <input value={form.slug} onChange={(e) => { setSlugTouched(true); update({ slug: e.target.value }); }} onBlur={(e) => update({ slug: slugify(e.target.value) })} className={field} placeholder="auto from name" data-testid="admin-category-slug" />
                </div>
                <div>
                  <label className={labelCls}>Group / Type</label>
                  <select value={form.type} onChange={(e) => update({ type: e.target.value })} className={field} data-testid="admin-category-type" disabled={!!editing}>
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Parent category (optional)</label>
                  <select value={form.parent || ''} onChange={(e) => update({ parent: e.target.value })} className={field}>
                    <option value="">— None —</option>
                    {cats.filter((c) => c.type === form.type && c.id !== editing?.id).map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2"><label className={labelCls}>Short description</label><input value={form.short_description} onChange={(e) => update({ short_description: e.target.value })} className={field} placeholder="One-line summary shown in listings" /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Full description</label><textarea rows={4} value={form.description} onChange={(e) => update({ description: e.target.value })} className={field} placeholder="Rich description shown on the category page" /></div>
              </div>
            )}

            {tab === 'images' && (
              <div className="grid gap-4 sm:grid-cols-2">
                <ImageField label="Thumbnail" value={form.image} onChange={(v) => update({ image: v })} ratio="aspect-square" testid="admin-category-thumb" />
                <ImageField label="OG / Social image" value={form.og_image} onChange={(v) => update({ og_image: v })} testid="admin-category-og" />
                <div className="sm:col-span-2"><ImageField label="Desktop hero / banner" value={form.hero_image} onChange={(v) => update({ hero_image: v })} testid="admin-category-hero" /></div>
                <div className="sm:col-span-2"><ImageField label="Mobile hero (optional)" value={form.hero_image_mobile} onChange={(v) => update({ hero_image_mobile: v })} ratio="aspect-[3/4]" testid="admin-category-hero-mobile" previewClass="max-w-[280px]" /></div>
              </div>
            )}

            {tab === 'display' && (
              <div className="grid gap-4">
                <label className="flex items-center justify-between rounded-xl border border-border p-3 text-sm">
                  <span><span className="font-medium">Active</span><span className="block text-xs text-muted-foreground">Visible on the storefront</span></span>
                  <input type="checkbox" checked={form.active} onChange={(e) => update({ active: e.target.checked })} className="h-5 w-5 accent-[hsl(var(--primary))]" data-testid="admin-category-active" />
                </label>
                <label className="flex items-center justify-between rounded-xl border border-border p-3 text-sm">
                  <span><span className="font-medium">Featured</span><span className="block text-xs text-muted-foreground">Highlighted in featured category rails</span></span>
                  <input type="checkbox" checked={form.featured} onChange={(e) => update({ featured: e.target.checked })} className="h-5 w-5 accent-[hsl(var(--gaze-gold))]" data-testid="admin-category-featured" />
                </label>
                <div><label className={labelCls}>Sort order</label><input type="number" value={form.sort_order} onChange={(e) => update({ sort_order: e.target.value })} className={field} data-testid="admin-category-sort" /><p className="mt-1 text-[11px] text-muted-foreground">Lower numbers appear first. You can also drag order using ↑ ↓ on the list.</p></div>
              </div>
            )}

            {tab === 'seo' && (
              <div className="grid gap-3">
                <div><div className="flex items-center justify-between"><label className={labelCls}>Meta title</label><Counter value={form.meta_title} min={40} max={60} /></div><input value={form.meta_title} onChange={(e) => update({ meta_title: e.target.value })} className={field} placeholder={`${form.name || 'Category'} · Gaze Decor`} data-testid="admin-category-meta-title" /></div>
                <div><div className="flex items-center justify-between"><label className={labelCls}>Meta description</label><Counter value={form.meta_description} min={70} max={160} /></div><textarea rows={2} value={form.meta_description} onChange={(e) => update({ meta_description: e.target.value })} className={field} placeholder="Falls back to short/full description if left empty" data-testid="admin-category-meta-desc" /></div>
                <div><label className={labelCls}>SEO keywords (comma separated, optional)</label><input value={form.keywords} onChange={(e) => update({ keywords: e.target.value })} className={field} placeholder="luxury decor, showpieces" /></div>
                <div><label className={labelCls}>Canonical URL (optional)</label><input value={form.canonical_url} onChange={(e) => update({ canonical_url: e.target.value })} className={field} placeholder="https://gazedecor.in/category/..." /></div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><label className={labelCls}>OG title (optional)</label><input value={form.og_title} onChange={(e) => update({ og_title: e.target.value })} className={field} placeholder="Falls back to meta title" /></div>
                  <div><label className={labelCls}>Robots</label>
                    <select value={form.robots} onChange={(e) => update({ robots: e.target.value })} className={field} data-testid="admin-category-robots">
                      <option value="index,follow">index, follow</option>
                      <option value="noindex,follow">noindex, follow</option>
                      <option value="index,nofollow">index, nofollow</option>
                      <option value="noindex,nofollow">noindex, nofollow</option>
                    </select>
                  </div>
                </div>
                <div><label className={labelCls}>OG description (optional)</label><textarea rows={2} value={form.og_description} onChange={(e) => update({ og_description: e.target.value })} className={field} placeholder="Falls back to meta description" /></div>
              </div>
            )}
          </div>

          <div className="mt-5 flex gap-3">
            <button onClick={() => requestClose(false)} className="flex-1 rounded-full border border-border py-3 text-sm font-semibold">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="admin-category-save" className="flex-1 rounded-full bg-[hsl(var(--primary))] py-3 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Saving…' : (editing ? 'Update Category' : 'Create Category')}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive-instead dialog when delete is blocked */}
      <Dialog open={!!archiveTarget} onOpenChange={(v) => !v && setArchiveTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{"Can't delete this category"}</DialogTitle>
            <DialogDescription className="sr-only">This category has products assigned and cannot be deleted</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{archiveTarget?.count}</span> product(s) are still assigned to
            {' '}<span className="font-medium text-foreground">{`"${archiveTarget?.cat?.name}"`}</span>. Deleting it would break those relationships.
            You can archive it instead — it will be hidden from the storefront but keep all product links intact.
          </p>
          <div className="mt-5 flex gap-3">
            <button onClick={() => setArchiveTarget(null)} className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold">Keep as is</button>
            <button onClick={archive} data-testid="admin-category-archive" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[hsl(var(--primary))] py-2.5 text-sm font-semibold text-white"><Archive size={15} /> Archive it</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
